<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { StepDurationProps } from '~/components/simulator/StepDuration/interface'
import { useSimulatorData } from '~/composables/useSimulatorData'

defineProps<StepDurationProps>()
const { t } = useI18n()
const {
  data,
  durationProps,
  amountByDuration,
  amountSliderProps,
  amountSliderValue,
  onDurationChange,
  onAmountChange,
} = useSimulatorData()

const container = ref<HTMLElement | null>(null)

defineExpose({
  container,
})

const label = computed(() => {
  return data.value.duration[0]
    ? t('simulator.durationStep.duration', {
        duration: data.value.duration[0],
      })
    : ''
})

const amountLabel = computed(() => {
  return data.value.duration[0]
    ? t('simulator.durationStep.amount', {
        amount: amountByDuration.value[data.value.duration[0]],
      })
    : ''
})
</script>

<template>
  <section
    ref="container"
    class="SimulatorStepDuration radius-m border-normal border-blue-01"
    tabindex="-1"
  >
    <molecules-ui-fieldset :legend="$t('simulator.durationStep.title')">
      <div class="SimulatorStepDuration__content g-4">
        <div class="SimulatorStepDuration__input">
          <p class="mb-3">{{ $t('simulator.durationStep.label') }}</p>
          <atoms-ui-range-slider
            v-model="data.duration"
            class="SimulatorStepDuration__slider"
            v-bind="durationProps"
            :label="label"
            @range-slider::on-change="(e?: number[]) => onDurationChange(e)"
          />
          <p
            v-if="errors?.duration"
            class="mt-2 label color-red-01"
          >
            {{ errors['duration'] }}
          </p>
        </div>
        <div class="SimulatorStepDuration__separator g-2">
          <span class="heading-3 color-blue-01">ou</span>
        </div>
        <div class="SimulatorStepDuration__input">
          <p class="mb-3">{{ $t('simulator.durationStep.amountLabel') }}</p>
          <atoms-ui-range-slider
            v-model="amountSliderValue"
            class="SimulatorStepDuration__slider"
            v-bind="amountSliderProps"
            :label="amountLabel"
            @range-slider::on-change="(e?: number[]) => onAmountChange(e)"
          />
          <p
            v-if="errors?.duration"
            class="mt-2 label color-red-01"
          >
            {{ errors['duration'] }}
          </p>
        </div>
      </div>
    </molecules-ui-fieldset>
  </section>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/colors';
@use 'config/text';
@use 'utilities/mq';

.SimulatorStepDuration {
  overflow: hidden;
  text-align: center;
}

.SimulatorStepDuration__content {
  display: grid;
  width: 100%;

  @include mq.media('>=small') {
    grid-template-columns: 1fr auto 1fr;
  }
}

.SimulatorStepDuration__input {
  @include mq.media('>=small') {
    &:nth-child(3) {
      grid-column: 3;
    }
  }
}

.SimulatorStepDuration__separator {
  display: flex;
  align-items: center;

  @include mq.media('>=small') {
    flex-direction: column;
  }

  &::before,
  &::after {
    content: '';
    display: block;
    flex: 1;
    height: 1px;
    background-color: map.get(colors.$blue, '01');

    @include mq.media('>=small') {
      width: 1px;
      height: auto;
    }
  }
}

.SimulatorStepDuration__slider {
  max-width: 360px;
  margin: 0 auto;

  :deep(.RangeSlider__label) {
    font-weight: map.get(text.$weight, 'bold');
  }
}
</style>
